import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataStorageService } from '@core/auth/services/data-storage.service';
import { retryWithBackoff } from '@core/rxjs/retry-with-backoff';
import { environment as env } from '@env';
import { defer, NEVER, Observable, of } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

interface HubInfo {
  uuid: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppHostService {
  constructor(private http: HttpClient, private dataStorage: DataStorageService) {}

  public initHostAndResolveCompanyUuid(): Observable<string> {
    return defer(() => {
      const dsCompanyUuid: string = this.dataStorage.getCompanyUuid();
      const host = `${window.location.origin}/`;
      // Check for companyUuid in URL (used for preview mode in iframe)
      const urlParams = new URLSearchParams(window.location.search);
      const urlCompanyUuid: string | null = urlParams.get('companyUuid');
      if (urlCompanyUuid) {
        this.dataStorage.setHost(host);
        this.dataStorage.setCompanyUuid(urlCompanyUuid);
        return of(urlCompanyUuid);
      }
      if (env.companyUuid) {
        // dev
        // no need to make request to api/hub,
        // when working locally or against staging apis from localhost,
        // will use companyUuid from environment.stage-dev.ts
        return this.resolveFromEnvironment(dsCompanyUuid, host);
      }
      if (host === this.dataStorage.getHost() && dsCompanyUuid) {
        // prod
        return of(dsCompanyUuid);
      }
      // prod
      return this.resolveFromHub(host);
    });
  }

  private resolveFromEnvironment(dsCompanyUuid: string, host: string): Observable<string> {
    if (dsCompanyUuid && dsCompanyUuid !== env.companyUuid) {
      // companyUuid changed in environment need to reset LS and reload page;
      // never emit — the page is reloading, so erroring here would flash the bootstrap error screen
      localStorage.clear();
      this.reloadPage();
      return NEVER;
    }
    this.dataStorage.setHost(host);
    this.dataStorage.setCompanyUuid(env.companyUuid);
    return of(this.dataStorage.getCompanyUuid());
  }

  private resolveFromHub(host: string): Observable<string> {
    // skip: bootstrap failure handling (retry + terminal error) is owned here, so the
    // interceptor's generic navigate-to-home error handling must stay out
    return this.http.get<HubInfo>(`${env.basePath}api/hub?appUrl=${host}`, { headers: { skip: 'true' } }).pipe(
      timeout(15000),
      retryWithBackoff({ count: 2, backoffMs: 1000 }),
      map((res: HubInfo) => {
        if (!res?.uuid) {
          // a 200 without a uuid must fail bootstrap like an error, not boot a degraded portal
          throw new Error('api/hub responded without a company uuid');
        }
        this.dataStorage.setHost(host);
        this.dataStorage.setCompanyUuid(res.uuid);
        return this.dataStorage.getCompanyUuid();
      })
    );
  }

  // seam for specs — jasmine cannot spy on window.location.reload
  protected reloadPage(): void {
    window.location.reload();
  }
}
